import React, { useState, useCallback, useEffect } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";
import moment from "moment"
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { Button, Typography, Spinner, Modal } from "stories/components";
import { Container, Row, Col, FormContainer, Card, CardBody, CardHeader } from "stories/layout";
import { Input, SelectMulti, Toggle, Radio, Checkbox } from "stories/forms";
import { useNotification } from "stories/components/Notification"
import SectorSelect from "components/Forms/SectorSelect/SectorSelect"
import { useAppContext } from "contexts/AppContext"
import SectorEdit from "views/app/SectorEdit/SectorEdit"



// import { httpGetCompany, httpCreateCompany, httpUpdateCompany, httpDeleteCompany } from "services/companies"
import { 
  httpCreateAccountSecurityRelation, 
  httpUpdateAccountSecurityRelation, 
  httpGetAccountSecurityRelation,
  httpGetPortfolioSecurityDimensions,
  httpCreatePortfolioSecurityDimension,
  httpUpdatePortfolioSecurityDimension,
  httpDeletePortfolioSecurityDimension
} from "services/accounts"

import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"
import PortfolioSelect from "components/Forms/PortfolioSelect/PortfolioSelect"

const defaultValues = {
  id: null,
  name: "",
}

const AccountSecurityRelationEdit = (props) => {
  
  const {
     values,
     touched,
     setTouched,
     errors,
     setErrors,
     handleChange,
     handleBlur,
     handleSubmit,
     setFieldValue,
     isValid,
     isTouched,
     isSubmitting,
     validateForm,
     preSetValues
   } = props;
   
   const {
      t,
      history,
      objectId,
      extraMargin,
      showExtraField,
      setShowExtraField,
      account,
      setSelectedSector,
      selectedSector,
      security,
      related_model_id,
      related_model_type,
      portfolioSecurityDimensions,
      setPortfolioSecurityDimensions,
      loadPortfolioSecurityDimensions,
   } = props;
   
   const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  const { getSectorName } = useAppContext()
  const getSectors = useStoreActions((actions) => actions.sectors.getSectors);




  // Modal: New/Edit Sector

  const [modalEditOpen, setModalEditOpen] = useState(false)
  const [modalEditId, setModalEditId] = useState()
  
  const toggleModalEdit = () => {
    setModalEditOpen(s => !s);
  }
  
  const openModalEdit = (id) => {
    setModalEditOpen(true)
    setModalEditId(id);
  }
  
  const modalEditResolve = () => {
    setModalEditOpen(false);
    setModalEditId(null);
    getSectors()
  }
  
  const modalEditReject = () => {
    setModalEditOpen(false);
    setModalEditId(null);
  }

  // Portfolio Security Dimension state
  const [newPortfolioDimension, setNewPortfolioDimension] = useState({ portfolio: null, dimension: '' });
  const [savingPortfolioDimension, setSavingPortfolioDimension] = useState(false);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);

  const handleAddPortfolioDimension = async () => {
    if (!newPortfolioDimension.portfolio || !newPortfolioDimension.dimension) {
      return;
    }
    setSavingPortfolioDimension(true);
    try {
      await httpCreatePortfolioSecurityDimension(account.id, {
        portfolio: newPortfolioDimension.portfolio,
        related_model_type: related_model_type,
        related_model_id: related_model_id,
        dimension: newPortfolioDimension.dimension
      });
      setNewPortfolioDimension({ portfolio: null, dimension: '' });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio lisätty") });
    } catch (error) {
      console.error("Error adding portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension lisäämisessä") });
    } finally {
      setSavingPortfolioDimension(false);
    }
  };

  const handleUpdatePortfolioDimension = async (item, newDimension) => {
    try {
      await httpUpdatePortfolioSecurityDimension(account.id, {
        portfolio: item.portfolio,
        related_model_type: related_model_type,
        related_model_id: related_model_id,
        dimension: newDimension
      });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio päivitetty") });
    } catch (error) {
      console.error("Error updating portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension päivittämisessä") });
    }
  };

  const handleDeletePortfolioDimension = async (item) => {
    try {
      await httpDeletePortfolioSecurityDimension(account.id, { id: item.id });
      loadPortfolioSecurityDimensions();
      props.notify({ title: '', message: t("Salkkudimensio poistettu") });
    } catch (error) {
      console.error("Error deleting portfolio dimension:", error);
      props.notify({ title: t("Virhe"), message: error?.data?.detail || t("Virhe salkkudimension poistamisessa") });
    }
  };

  const getPortfolioName = (portfolioId) => {
    const portfolio = portfolios?.find(p => p.id === portfolioId);
    return portfolio?.name || portfolioId;
  };

  // Get portfolios that don't have a dimension set yet
  const getAvailablePortfolios = () => {
    const usedPortfolioIds = portfolioSecurityDimensions?.map(d => d.portfolio) || [];
    return portfolios?.filter(p => !usedPortfolioIds.includes(p.id)) || [];
  };

  
  const onSubmit = (e) => {
    e.preventDefault();
    validateForm().then(res => {
      if (Object.keys(res).length === 0) {
        handleSubmit()
      }
      else {
        setTouched(res);
        setErrors({
          ...res,
          general: t("Please check the form errors", "Tarkista lomakkeen virheet")
        })
      }
    })
  }
  
  const handleCancel = () => {
    props.handleReject()
  }
  
//   const handleDelete = () => {
//     httpDeleteCompany(objectId).then(() => {
//         props.handleResolve()
//         props.notify({ title:t("Company"), message:t("Poistettu")})
//     })
//   }

  const handleExtraFieldChange = (event) => {
    setShowExtraField(event.target.checked)
  }

  const handleSectorChange = (name, value) => {
    setSelectedSector(s => ({
      ...s,
      [name]: value
    }))
  }

  const getSectorSelect = useCallback(() => {
    return (
      <SectorSelect
        {...getFormikInputProps("sector_select")}
        label=" "
        multiple={false}
        onChange={handleSectorChange}
        value={selectedSector?.sector_select}
        setFieldValue={handleSectorChange}
      />
    )
  }, [selectedSector])

  
  return (
    <div >
            <Row className="mb-1 mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("account_security_name", "Nimi")}</label>
              </Col>
              <Col >
              <Input
                  name= "name"
                  label=""
                  value={security?.name}
                  disabled={true}

              />
              </Col>
          </Row>         
           <Row className="mb-1 mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("account_security_symbol", "Tunnus")}</label>
              </Col>
              <Col >
              <Input
                  name = "symbol"
                  label=""
                  value={security?.ticker_symbol ? security?.ticker_symbol : security?.symbol}
                  disabled={true}

              />
              </Col>
          </Row>
          <Row className="mb-1 mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("account_security_stock_market", "Pörssi")}</label>
              </Col>
              <Col >
              <Input
                  name = "stock_market"
                  label=""
                  value={security?.stock_market}
                  disabled={true}

              />
              </Col>
          </Row>
            {security?.public_sector ? (
                <Row className="mb-1 mt-3">
                    <Col sm={12} lg={4} className="d-flex align-items-center">
                    <label className="mb-1 pr-1 header">{t("account_security_sector", "Toimiala")}</label>
                    </Col>
                    <Col >
                    <Input
                        name = "stock_market"
                        label=""
                        value={t(`sector_select_1_${security?.public_sector}`,getSectorName(`1_${security?.public_sector}`))}
                        disabled={true}
    
                    />
                    </Col>
                </Row>
            ):(
                <>
                    <Row className="mb-1 mt-3">
                        <Col sm={12} lg={4} className="d-flex align-items-center">
                            <label className="mb-1 pr-1 header">{t("account_security_edit_sector", "Toimiala")}</label>
                        </Col>
                        <Col >
                            {getSectorSelect()}
                        </Col>
                    </Row>
                    <Row className="mb-3">
                            <Col sm={12} lg={4} className="d-flex align-items-center">
                            </Col>
                            <Col>
                            <div  calssName="text-blue" onClick={() => openModalEdit()} style={{ cursor: 'pointer' }}>
                            <Typography variant="small">  {t("sector_create_here","Luo toimiala tästä")}</Typography>
                            </div>
                            </Col>
                        </Row>
                </>
            )}

            {security?.isin  ? (
                <Row className="mb-1 mt-3">
                    <Col sm={12} lg={4} className="d-flex align-items-center">
                    <label className="mb-1 pr-1 header">{t("account_security_isin", "ISIN")}</label>
                    </Col>
                    <Col >
                      <Input
                          {...getFormikInputProps("isin")} 
                          label=""
                          value={security?.isin }
                          // disabled={true}
                        />
                    </Col>
                </Row>
            ):(
                <>
                    <Row className="mb-1 mt-3">
                        <Col sm={12} lg={4} className="d-flex align-items-center">
                            <label className="mb-1 pr-1 header">{t("account_security_edit_isin", "ISIN")}</label>
                        </Col>
                        <Col >
                        <Input
                          {...getFormikInputProps("isin")} 
                          label=""
                        />
                        </Col>
                    </Row>
              
                </>
            )}
            { security?.total_issue_custom && (

          <Row className="mb-1 mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("accoutn_security_edit_total_issue", "Arvopaperin koko")}</label>
              </Col>
              <Col >
              <Input
                  {...getFormikInputProps("total_issue")} 
                  label=""
                  type="number"

                  />
              </Col>
          </Row>
            )
}

          {  account?.show_relation_to_security &&
            <>
              <Row className="mb-1 mt-3">
                  <Col sm={12} lg={4} className="d-flex align-items-center">
                  <label className="mb-1 pr-1 header">{t("account_security_edit_dimensio", "Dimensio")}</label>
                  </Col>
                  <Col >
                  <Input
                      {...getFormikInputProps("dimension")} 
                      label=""
                      />
                  </Col>
              </Row>
              <Row className="mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
                  <label className="mb-1 pr-1 header">{t("account_security_edit_sector_custom_symbol", "Oma tunnus")}</label>
                  </Col>
              <Col >
              <Input
                  {...getFormikInputProps("custom_symbol")} 
                  label=""
                  />
              </Col>
              </Row>

              {/* Portfolio Security Dimensions Section */}
              <Row className="mb-1 mt-3">
                <Col>
                  <label className="mb-1 pr-1 header header-bold">{t("account_security_edit_portfolio_dimensions", "Salkkudimensio")}</label>
                  <Typography variant="small" className="text-muted d-block">
                    {t("account_security_edit_portfolio_dimensions_info", "Jos salkulle on määritetty oma dimensio, se yliajaa yllä olevan oletusarvon.")}
                  </Typography>
                </Col>
              </Row>

              {/* Existing portfolio dimensions */}
              {portfolioSecurityDimensions && portfolioSecurityDimensions.length > 0 && (
                <div className="mt-2">
                  {portfolioSecurityDimensions.map((item, index) => (
                    <Row key={item.id || index} className="mb-2 align-items-center">
                      <Col sm={12} lg={4}>
                        <Input
                          name={`portfolio_name_${index}`}
                          label=""
                          value={item.portfolio_name || getPortfolioName(item.portfolio)}
                          disabled={true}
                        />
                      </Col>
                      <Col sm={8} lg={5}>
                        <Input
                          name={`portfolio_dimension_${index}`}
                          label=""
                          defaultValue={item.dimension}
                          onBlur={(e) => {
                            if (e.target.value !== item.dimension) {
                              handleUpdatePortfolioDimension(item, e.target.value);
                            }
                          }}
                        />
                      </Col>
                      <Col sm={4} lg={3}>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleDeletePortfolioDimension(item)}
                        >
                          {t("Poista")}
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              )}

              {/* Add new portfolio dimension */}
              {getAvailablePortfolios().length > 0 && (
                <Row className="mb-2 mt-3 align-items-center">
                  <Col sm={12} lg={4}>
                    <Input 
                      type="select"
                      name="new_portfolio_dimension_portfolio"
                      label=""
                      value={newPortfolioDimension.portfolio || ''}
                      onChange={(e) => setNewPortfolioDimension(prev => ({ ...prev, portfolio: parseInt(e.target.value) || null }))}
                    >
                      <option value="">{t("Valitse salkku")}</option>
                      {getAvailablePortfolios().map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Input>
                  </Col>
                  <Col sm={8} lg={5}>
                    <Input
                      name="new_portfolio_dimension_value"
                      label=""
                      placeholder={t("Dimensio")}
                      value={newPortfolioDimension.dimension}
                      onChange={(e) => setNewPortfolioDimension(prev => ({ ...prev, dimension: e.target.value }))}
                    />
                  </Col>
                  <Col sm={4} lg={3}>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      disabled={!newPortfolioDimension.portfolio || !newPortfolioDimension.dimension || savingPortfolioDimension}
                      onClick={handleAddPortfolioDimension}
                    >
                      {savingPortfolioDimension ? t("Tallentaa...") : t("Lisää")}
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          }



          {/* <Row className="mt-3">
              <Col sm={12} lg={4} className="d-flex align-items-center">
              <label className="mb-1 pr-1 header">{t("portfolio_edit_external_system_1_export", "Vie tiedot")}</label>
              </Col>
              <Col>
                <Checkbox 
                  id={"export_external_first_system"}
                  {...getFormikInputProps("export_external_first_system")}
                  value= {values?.export_external_first_system}
                  checked={values?.export_external_first_system}
                  className="zIndexZero"
                  required
                  label={<>{t("portfolio_edit_extra_info", '')}</>}
                />
                </Col>
          </Row> */}
          <Row className="mt-3">

        <Col className="mb-3">
          <Button onClick={handleCancel} variant="secondary" className="sk-modal-dismiss">{t("Close","Sulje")}</Button>
        </Col>
     
        <Col className="mb-3 text-right">

            <Button variant="primary" disabled={isSubmitting} onClick={onSubmit}>
              {t("Save","Tallenna")}
            </Button>
          
          { Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography> }
        </Col>
  
      </Row> 



      <Modal
        title={t("sector_title","Toimiala")}
        isOpen={modalEditOpen}
        toggleModal={toggleModalEdit}
        
        >
        <SectorEdit
            objectId={modalEditId}
            handleResolve={modalEditResolve}
            handleReject={modalEditReject}
            extraMargin={true}
        />
        </Modal>
        
    </div>
  );
}

const AccountSecurityRelationEditFormik = withFormik({
    enableReinitialize: true,
    validateOnMount: true,
    mapPropsToValues: props => {
      const { preSetValues } = props;
      if ( preSetValues) {
        return preSetValues;
      } else {
        return {
          ...defaultValues
        }
      }
    },
    validationSchema: props => {
      const {t} = props;
      const required = t("The field is required");
      const specialCharRegex = /^[a-zA-Z0-9\s]+$/; // Tämä sallii vain kirjaimet, numerot ja välilyönnit
      return Yup.object().shape({
        // external_first_system_name: Yup.string().required(required).nullable(),
        dimension: Yup.string()
          .matches(specialCharRegex, t("account_security_dimension","Dimension ei saa sisältää erikoismerkkejä"))
          .nullable(),
      });
    },
    handleSubmit: (values, { setSubmitting, handleResolve, setErrors,  props }) => {
      const {t, portfolio, account, related_model_type, selectedSector, related_model_id, setRelationId, relationId} = props;
      console.log("account", account);

      let data = {
        ...values,
        related_model_type: related_model_type,
        related_model_id: related_model_id,
        general_sector: selectedSector?.sector_select?.id,
        dimension: values?.dimension || null,
        custom_symbol: values?.custom_symbol || null,
        total_issue: values?.total_issue || null,
        isin: values?.isin || null,
        // account: account?.id,
      };
      
      if (values?.id || relationId) {
        httpUpdateAccountSecurityRelation(account.id, data).then(res => {
          setSubmitting(false);
          props.notify({ title:t(""), message:t("Updated")})
          props.handleResolve()
          
        }, error => {
          setSubmitting(false);
          if (error?.data?.detail || error?.data?.general) {
            setErrors({
              general: error?.data?.detail,
              general_sector: error?.data?.general_sector || null,
              dimension: error?.data?.dimension || null,
              custom_symbol: error?.data?.custom_symbol || null,
              total_issue: error?.data?.total_issue || null,
              isin: error?.data?.isin || null,

            })
          }
          else {
            setErrors({
              general: t("Unknown error. Please try again later or contact support."),
              general_sector: error?.data?.general_sector || null,
              dimension: error?.data?.dimension || null,
              custom_symbol: error?.data?.custom_symbol || null,
              total_issue: error?.data?.total_issue || null,
              isin: error?.data?.isin || null,
            })
          }
        })
      }
      else {
        httpCreateAccountSecurityRelation(account.id, data).then(res => {
          setSubmitting(false);
          props.notify({ title:t(""), message:t("Updated")})
          setRelationId(res?.data?.message?.id)
          props.handleResolve()
        }, error => {
          setSubmitting(false);
          if (error?.data?.detail) {
            setErrors({
              general: error?.data?.detail,
              general_sector: error?.data?.general_sector || null,
              dimension: error?.data?.dimension || null,
              custom_symbol: error?.data?.custom_symbol || null,
              total_issue: error?.data?.total_issue || null,
              isin: error?.data?.isin || null,
            })
          }
          else {
            setErrors({
              general: t("Unknown error. Please try again later or contact support."),
              general_sector: error?.data?.general_sector || null,
              dimension: error?.data?.dimension || null,
              custom_symbol: error?.data?.custom_symbol || null,
              total_issue: error?.data?.total_issue || null,
              isin: error?.data?.isin || null,
            })
          }
        })
      }
      
      
    },
    displayName: "BasicForm"
  
})(AccountSecurityRelationEdit)
    
    
const AccountSecurityRelationEditView = ({history,handleResolve, handleReject, handleCancel, objectId, security, related_model_type, related_model_id, modalOpen,extraMargin, ...rest}) => {
  // API requests here
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preSetValues, setPreSetValues] = useState();
  const { notify } = useNotification(); // import { useNotification } from "stories/components/Notification"
  const account = useStoreState((state) => state.account?.account);
  const [showExtraField, setShowExtraField] = useState(false);
  const [selectedSector, setSelectedSector] = useState({sector_select: []});
  const { getSectorName } = useAppContext()
  const [relationId, setRelationId] = useState()
  const [portfolioSecurityDimensions, setPortfolioSecurityDimensions] = useState([]);

  const loadPortfolioSecurityDimensions = useCallback(() => {
    if (account?.id && related_model_type && related_model_id) {
      httpGetPortfolioSecurityDimensions(account.id, {
        related_model_type: related_model_type,
        related_model_id: related_model_id
      }).then(res => {
        setPortfolioSecurityDimensions(res?.data || []);
      }).catch(err => {
        console.error("Error loading portfolio security dimensions:", err);
        setPortfolioSecurityDimensions([]);
      });
    }
  }, [account?.id, related_model_type, related_model_id]);

  
  const getObject = (id) => {
    
    setLoading(true)
    const params = {related_model_id: related_model_id, related_model_type: related_model_type}
    
    httpGetAccountSecurityRelation(account.id, params).then(res => {
      
      setPreSetValues({
        ...defaultValues,
        ...res?.data,
      });
      let sector = null
      if (res?.data?.private_sector){
        //add 1_ to private_sector id like 1_2322
        sector =`2_${res?.data?.private_sector}`
      }
      if (res?.data?.public_sector){
        sector =`1_${res?.data?.public_sector}`
      }
      setSelectedSector({
        sector_select: sector ? [{id: sector, label: t(`sector_select_1_${sector}`,getSectorName(`1_${sector}`)), name:t(`sector_select_1_${sector}`,getSectorName(`1_${sector}`)) }] : []
      })
    
    }).finally(response => {
      setLoading(false);
    })
    
  }
  
  
  useEffect(() => {
    if (objectId && parseInt(objectId) > -1) {
      getObject(objectId)
    }
  }, [objectId, modalOpen])

  // Load portfolio security dimensions when component mounts or related_model changes
  useEffect(() => {
    if (account?.id && related_model_type && related_model_id) {
      loadPortfolioSecurityDimensions();
    }
  }, [account?.id, related_model_type, related_model_id, loadPortfolioSecurityDimensions])
  
  
  if (loading) {
    return (
      <Container fluid>
        <Spinner />
      </Container>
    )
  }

  return (
    <AccountSecurityRelationEditFormik
      t={t} 
      history={history} 
      preSetValues={preSetValues} 
      notify={notify} 
      objectId={objectId}
      account={account}
      extraMargin={extraMargin}
      showExtraField={showExtraField}
      setShowExtraField={setShowExtraField}
      related_model_type={related_model_type}
      related_model_id={related_model_id}
      setSelectedSector={setSelectedSector}
      selectedSector={selectedSector}
      setRelationId={setRelationId}
      relationId={relationId}
      security={security}
      portfolioSecurityDimensions={portfolioSecurityDimensions}
      setPortfolioSecurityDimensions={setPortfolioSecurityDimensions}
      loadPortfolioSecurityDimensions={loadPortfolioSecurityDimensions}
      handleResolve={handleResolve}
      handleReject={handleReject}
      handleCancel={handleCancel}
      {...rest} 
    />
  )
  
}

export default AccountSecurityRelationEditView;
